import { Box, Flex, FormControl, FormLabel, Spinner, Switch, Text } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractDurationType, type SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { useDebouncedSave } from "../hooks/useDebouncedSave";
import { useSaveField } from "../hooks/useSaveField";
import { buildDurationUpdate } from "../utils/mapToBffCommand";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
}

export const ContractDurationField = ({ eventId, event }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);
  const duration = event.eventData.contractDuration;

  const [contractStartDate, setContractStartDate] = useState<string | undefined>(
    duration?.type === ContractDurationType.FixedDates || duration?.type === ContractDurationType.PeriodBased
      ? duration.startDate ?? undefined
      : undefined
  );
  const [contractEndDate, setContractEndDate] = useState<string | undefined>(
    duration?.type === ContractDurationType.FixedDates ? duration.endDate ?? undefined : undefined
  );
  const [isOpenEnded, setIsOpenEnded] = useState(duration?.type === ContractDurationType.OpenEnded);

  const debouncedSaveDuration = useDebouncedSave(
    useCallback(
      (startDate: string | null, endDate: string | null, openEnded: boolean) =>
        saveField({ contractDuration: buildDurationUpdate({ startDate, endDate, isOpenEnded: openEnded }) }),
      [saveField]
    )
  );

  return (
    <FormControl>
      <FormLabel>{t("Contract duration")} {isSaving("contractDuration") && <Spinner size="xs" />}</FormLabel>
      <Flex align="center" gap="2" mb="3">
        <Switch
          colorScheme="teal"
          isChecked={isOpenEnded}
          onChange={(e) => {
            const checked = e.target.checked;
            setIsOpenEnded(checked);
            debouncedSaveDuration(contractStartDate ?? null, checked ? null : contractEndDate ?? null, checked);
          }}
        />
        <Text fontSize="sm">{t("Running agreement (open-ended)")}</Text>
      </Flex>
      <Flex gap="4">
        <Box flex="1">
          <FormLabel fontSize="sm">{t("Start date")}</FormLabel>
          <ThemedDatePicker
            defaultDate={contractStartDate}
            allowPastDates
            onChange={(date) => {
              setContractStartDate(date);
              debouncedSaveDuration(date ?? null, contractEndDate ?? null, isOpenEnded);
            }}
          />
        </Box>
        {!isOpenEnded && (
          <Box flex="1">
            <FormLabel fontSize="sm">{t("End date")}</FormLabel>
            <ThemedDatePicker
              defaultDate={contractEndDate}
              allowPastDates
              onChange={(date) => {
                setContractEndDate(date);
                debouncedSaveDuration(contractStartDate ?? null, date ?? null, isOpenEnded);
              }}
            />
            {contractStartDate && !contractEndDate && (
              <Text fontSize="xs" color="smWarning" mt="1">
                {t("End date is required when not open-ended")}
              </Text>
            )}
          </Box>
        )}
      </Flex>
    </FormControl>
  );
};
